import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Check, Loader2, RotateCcw, Trash2, X } from "lucide-react";
import { API, auth, TOK, SERIF, SANS } from "./shared";

const pill = (on) => ({
  border: `1px solid ${on ? TOK.gold : TOK.goldMist}`, color: on ? TOK.ink : TOK.creamDim,
  background: on ? TOK.gold : "transparent", fontFamily: SANS, fontSize: "0.6rem", letterSpacing: "0.22em",
});
export const Pill = ({ on, children, testId, ...p }) => (
  <button type="button" data-testid={testId} {...p} style={pill(on)}
    className="rounded-full px-4 py-2 uppercase inline-flex items-center gap-1.5 transition-colors duration-200 hover:opacity-90 disabled:opacity-40">
    {children}
  </button>
);

const Box = ({ on }) => (
  <span className="w-5 h-5 shrink-0 rounded-md inline-flex items-center justify-center transition-colors duration-150"
    style={{ border: `1px solid ${on ? TOK.gold : TOK.goldMist}`, background: on ? TOK.gold : "transparent" }}>
    {on && <Check size={13} color={TOK.ink} />}
  </span>
);

const useSelection = (ids) => {
  const [sel, setSel] = useState(new Set());
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const all = sel.size === ids.length && ids.length > 0;
  const toggleAll = () => setSel(all ? new Set() : new Set(ids));
  return { sel, toggle, all, toggleAll, clear: () => setSel(new Set()) };
};

const SelectRow = ({ id, title, sub, on, onToggle, testId }) => (
  <li>
    <button type="button" onClick={() => onToggle(id)} data-testid={testId}
      className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left transition-colors duration-150"
      style={{ background: on ? "rgba(215,187,122,0.10)" : "rgba(245,224,173,0.03)", border: `1px solid ${on ? TOK.gold : TOK.goldMist}` }}>
      <Box on={on} />
      <span className="min-w-0 flex-1">
        <span className="block truncate" style={{ fontFamily: SERIF, color: TOK.cream, fontSize: "1.05rem" }}>{title}</span>
        {sub && <span className="block text-[0.62rem] uppercase tracking-[0.2em] mt-0.5" style={{ color: TOK.creamMist, fontFamily: SANS }}>{sub}</span>}
      </span>
    </button>
  </li>
);

const ActionBar = ({ children, testId }) => (
  <div className="sticky bottom-3 z-20 mt-3 rounded-2xl px-4 py-3 flex flex-wrap items-center gap-2 justify-between"
    style={{ background: "rgba(13,31,58,0.92)", border: `1px solid ${TOK.goldMist}`, backdropFilter: "blur(14px)" }} data-testid={testId}>
    {children}
  </div>
);

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");
const FAIL = "That didn't go through. Please check your connection and try again.";

// Pick many installations at once and move them to Recently deleted.
export const BulkSelectList = ({ items, onDone, onCancel }) => {
  const { sel, toggle, all, toggleAll } = useSelection(items.map((j) => j.id));
  const [busy, setBusy] = useState(false);
  const [arm, setArm] = useState(false);
  useEffect(() => { if (!arm) return undefined; const t = setTimeout(() => setArm(false), 5000); return () => clearTimeout(t); }, [arm]);
  // Two taps instead of a browser pop-up: iPhones can silently block repeated pop-ups.
  const trash = async () => {
    if (!arm) { setArm(true); return; }
    setArm(false);
    setBusy(true);
    try { await axios.post(`${API}/api/audio-journeys/bulk-trash`, { ids: [...sel] }, auth()); onDone(); }
    catch { window.alert(FAIL); } finally { setBusy(false); }
  };
  return (
    <div data-testid="library-bulk-select">
      <ul className="space-y-2">
        {items.map((j) => (
          <SelectRow key={j.id} id={j.id} title={j.name || "Untitled"} on={sel.has(j.id)} onToggle={toggle}
            sub={[(j.affirmation_theme || "").replace(/_/g, " "), j.audio_status !== "ready" && j.audio_status, fmtDate(j.created_at)].filter(Boolean).join(" · ")}
            testId={`library-bulk-row-${j.id}`} />
        ))}
      </ul>
      <ActionBar testId="library-bulk-bar">
        <span className="flex items-center gap-2">
          <Pill on={all} onClick={toggleAll} testId="library-bulk-select-all">{all ? "Clear all" : "Select all"}</Pill>
          <span className="text-xs" style={{ color: TOK.creamDim, fontFamily: SANS }} data-testid="library-bulk-count">{sel.size} selected</span>
        </span>
        <span className="flex items-center gap-2">
          <Pill onClick={onCancel} testId="library-bulk-cancel"><X size={12} /> Done</Pill>
          <Pill on disabled={!sel.size || busy} onClick={trash} testId="library-bulk-delete">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} {arm ? `Tap again · delete ${sel.size}` : "Delete"}
          </Pill>
        </span>
      </ActionBar>
    </div>
  );
};

// Installations removed in the last 30 days. Restore or delete for good.
export const RecentlyDeleted = ({ refreshKey, onChanged }) => {
  const [data, setData] = useState({ items: [], days: 30 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [armPurge, setArmPurge] = useState(false);
  const load = useCallback(() => axios.get(`${API}/api/audio-journeys/trash`, auth()).then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const { sel, toggle, all, toggleAll, clear } = useSelection(data.items.map((j) => j.id));
  if (!data.items.length) return null;
  const left = (iso) => Math.max(0, data.days - Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  const act = async (kind) => {
    if (kind === "purge" && !armPurge) { setArmPurge(true); setTimeout(() => setArmPurge(false), 5000); return; }
    setArmPurge(false);
    setBusy(kind);
    try {
      await axios.post(`${API}/api/audio-journeys/${kind === "purge" ? "bulk-purge" : "bulk-restore"}`, { ids: [...sel] }, auth());
      clear(); await load(); onChanged?.();
    } catch { window.alert(FAIL); } finally { setBusy(""); }
  };
  return (
    <section className="mb-10" data-testid="library-recently-deleted">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="library-recently-deleted-toggle"
        className="w-full flex items-center justify-between rounded-2xl px-5 py-3"
        style={{ border: `1px dashed ${TOK.goldMist}`, color: TOK.creamDim, fontFamily: SANS }}>
        <span className="text-[0.62rem] uppercase tracking-[0.28em] inline-flex items-center gap-2"><Trash2 size={12} /> Recently deleted · {data.items.length}</span>
        <span className="text-[0.62rem] uppercase tracking-[0.22em]">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <>
          <p className="mt-3 mb-3 text-xs italic" style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.95rem" }}>
            Kept for {data.days} days, then removed for good.
          </p>
          <ul className="space-y-2">
            {data.items.map((j) => (
              <SelectRow key={j.id} id={j.id} title={j.name || "Untitled"} on={sel.has(j.id)} onToggle={toggle}
                sub={`${left(j.trashed_at)} days left`} testId={`library-trash-row-${j.id}`} />
            ))}
          </ul>
          <ActionBar testId="library-trash-bar">
            <Pill on={all} onClick={toggleAll} testId="library-trash-select-all">{all ? "Clear all" : "Select all"}</Pill>
            <span className="flex items-center gap-2">
              <Pill disabled={!sel.size || !!busy} onClick={() => act("restore")} testId="library-trash-restore">
                {busy === "restore" ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Restore
              </Pill>
              <Pill on disabled={!sel.size || !!busy} onClick={() => act("purge")} testId="library-trash-purge">
                {busy === "purge" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} {armPurge ? "Tap again · can't be undone" : "Delete for good"}
              </Pill>
            </span>
          </ActionBar>
        </>
      )}
    </section>
  );
};
